/*
 * A thread, in the details column (UX §4.5, VISUAL.md §7.5). The root at the top, its replies below
 * it, and a composer whose draft is the thread's own — `c:<conv>:t:<root>`, which is what
 * `dispatch.ts` reads the reply's `threadRootId` back out of.
 *
 * The pane is deliberately the conversation's surface again rather than a second kind of screen:
 * the same `buildRows`, the same `WindowedList` with its roving tabindex and prepend correction,
 * the same `useMessageActions`, the same `MessageRow` with the same menu, the same `SelectionBar`.
 * What it is not is a second timeline — a reply has no thread of its own, so the pane passes no
 * `openThread`, and the root wears `hideThreadFooter` because its footer would point at the pane
 * the reader is already in.
 *
 * **One scroller.** The root is the pane's first row, the "11 replies" rule is the row after it and
 * the replies follow: a long root therefore scrolls past instead of taking the pane, which is what it
 * did while it stood outside the scroller — 550 px of root in an 828 px pane left the reply list
 * 55 px and not one reply on screen (D-05 D2).
 *
 * **Where the pane opens** (X-B1 D4, G-95). On the first reply this reader has not read, under a
 * *New replies* rule; on the newest reply when there is none, with the root one scroll up. What
 * somebody opens a thread *for* is the part of it they have not had — landing on the root answered a
 * long root instead, and with eleven replies it put the newest reply, the reason the pane was opened,
 * below the fold behind a "Jump to the latest" pill (`E-04-phone-thread-sheet`). A thread short
 * enough to fit needs no landing and gets none, and the pill then says what it means: the offer a
 * reader who has scrolled *up* wants.
 *
 * Which replies are new is the footer dot's own rule read one reply at a time (`threadUnread`): a
 * thread carries no read cursor of its own, so the mark is the later of the conversation's cursor and
 * this reader's own last reply here. It is frozen when the pane opens, the way the timeline's unread
 * divider is pinned at the seq it was created with — a rule that followed the cursor would erase
 * itself under the reader.
 *
 * **One state at a time** (X-B3 D3). A `?thread=` that names no root a reader may see — a stale
 * permalink, a deleted root — used to show an alert, "No replies yet." and a composer named "Reply to
 * 's thread" all at once. The pane draws the failure alone until it has a root: the empty rule is
 * about a thread that *has* one, and a reply box is only offered for something to reply to.
 *
 * The root is a row like any other and keeps every affordance a reply has, the profile button
 * included: somebody who opens a thread from Activity has no other way to the author from here
 * (X-A2 P5).
 *
 * Two focus rules are the pane's own and both are one mechanism rather than two. The box takes the
 * caret through `autoFocus` when the pane opens; the `ref` exists only so that `Escape` in the
 * reply list can put the focus back into it. And the composer is given no `onCancel`, so an Escape
 * it does not use is left unprevented and reaches the shell's region listener, which closes the
 * column (EDITOR §8, UX §2.3).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Composer } from "../../../core/composer/Composer.tsx"
import type { ComposerHandle } from "../../../core/composer/types.ts"
import type { AttachmentRef } from "../../../core/drafts/types.ts"
import { EmojiPicker } from "../../../core/emoji/EmojiPicker.tsx"
import { formatDate, isoDateTime } from "../../../core/i18n/format.ts"
import type { ListHandle } from "../../../core/list/types.ts"
import { RenderContextProvider } from "../../../core/render/context.ts"
import { MarkdownPreview } from "../../../core/render/MarkdownBody.tsx"
import { inPlaceClick, useDetails, type DetailsRequest } from "../../../core/shell/index.ts"
import { addressOf } from "../../../core/store/conversationName.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { LiveRegion } from "../../../lib/liveRegion.tsx"
import { Button, ListState } from "../../../ui/index.ts"
import { previewMarkdown } from "../api.ts"
import { MessageEditor } from "../attachments/index.ts"
import { DeleteConfirm } from "../conversation/DeleteConfirm.tsx"
import { JoinBanner } from "../conversation/JoinBanner.tsx"
import { chatSourcesFor } from "../conversation/sources.ts"
import { ThreadTypingLine } from "../conversation/TypingLine.tsx"
import { chatDeps } from "../deps.tsx"
import { t } from "../i18n/t.ts"
import { buildRows } from "../list/buildRows.ts"
import { dayLabel } from "../list/dayLabel.ts"
import { MessageList } from "../list/MessageList.tsx"
import type { Row } from "../list/types.ts"
import { MessageRow } from "../message/MessageRow.tsx"
import { PendingRow } from "../message/PendingRow.tsx"
import { SelectionBar } from "../message/SelectionBar.tsx"
import { useMessageActions } from "../message/useMessageActions.ts"
import { conversationPath, dmPath, navigateTo } from "../navigation.ts"
import "../render/chat.css"
import { useRenderContextValue } from "../render/renderContext.ts"
import { loadOlderReplies, loadThread, visitThread } from "../state/history.ts"
import { chatSlice, threadOf, threadReadSeq } from "../state/selectors.ts"
import type { ConversationView as ChatConversation, MessageView } from "../wire/views.ts"
import { readThreadParams } from "./openThread.ts"
import "./thread.css"

const NO_REPLIES: MessageView[] = []
const EMPTY_SELECTION: readonly number[] = []

/** The registered view: the shell hands the request's parameters, the pane reads them once. */
export function ThreadView({ params }: { params: DetailsRequest["params"] }) {
	const { conv, rootId, highlightSeq } = readThreadParams(params)
	return <ThreadPane conv={conv} rootId={rootId} highlightSeq={highlightSeq} />
}

/*
 * The thread's own chrome in the details column's header row (VISUAL.md §7.7): the place the thread
 * belongs to, as the tag every surface names a place with. It stands *beside* the heading rather than
 * under it, because the heading says "Thread" and the question a reader has next is "which
 * conversation's?" — one line, not two. The words are the address; the link's name says "in
 * #design", which is the sentence the heading continues.
 */
export function ThreadPlace({ params }: { params: DetailsRequest["params"] }) {
	const { conv } = readThreadParams(params)
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)
	const meta = useModel((model) => model.conversations[conv]?.meta) as ChatConversation | undefined
	if (meta === undefined) return null
	const name = addressOf(meta, users, meId)
	const href = meta.dmHandle ? dmPath(meta.dmHandle) : conversationPath(meta.slug ?? "")
	return (
		<p className="tb-thread__place">
			<a
				className="tb-tag"
				href={href}
				onClick={inPlaceClick(href)}
				aria-label={t("chat.thread.in", { conversation: name })}
			>
				{name}
			</a>
		</p>
	)
}

export interface ThreadPaneProps {
	conv: ConversationId
	rootId: number
	/** A reply a permalink named: it is scrolled to and flashed once the page is in (UX §4.5). */
	highlightSeq?: number | undefined
}

export function ThreadPane({ conv, rootId, highlightSeq }: ThreadPaneProps) {
	const thread = useModel((model) => threadOf(model, rootId))
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)
	const outbox = useModel((model) => model.outbox)
	// Whether the reader is in the conversation: one who reads it through a group, or as an
	// administrator from outside, may not reply, so the pane draws the way in instead of a box
	// whose every send the server refuses.
	const member = useModel((model) => chatSlice(model).self[conv]?.state === "active")

	// This conversation's row and not the whole map: the map is replaced by every event in every
	// conversation, and what the pane reads of it is one name (`ThreadPlace` above subscribes the
	// same way).
	const meta = useModel((model) => model.conversations[conv]?.meta) as ChatConversation | undefined
	// A thread names the conversation it belongs to as a *place*, which is "#allgemein" and not
	// "Allgemein" — the naming ladder every surface reads (`core/store/conversationName.ts`). It is
	// memoised because the pane both renders it and passes it into three names: as a bare expression
	// the compiler pulls it into the scope around them and gives up on the callbacks in between.
	const conversationName = useMemo(
		() => (meta ? addressOf(meta, users, meId) : ""),
		[meta, users, meId],
	)
	const details = useDetails()

	const listRef = useRef<ListHandle>(null)
	const composerRef = useRef<ComposerHandle>(null)
	const [announcement, setAnnouncement] = useState("")
	const [editing, setEditing] = useState(0)
	const [selected, setSelected] = useState<readonly number[]>(EMPTY_SELECTION)
	const [confirmDelete, setConfirmDelete] = useState<MessageView | null>(null)
	// "Also send to #allgemein", unticked for every reply: it is a decision about *this* answer, and
	// a box that stayed ticked would quietly turn a thread into a second channel (X-A1 A4).
	const [alsoToChannel, setAlsoToChannel] = useState(false)
	const deps = chatDeps()

	const root = thread?.root
	const status = thread?.status ?? "cold"
	useEffect(() => {
		if (status === "cold") void loadThread(conv, rootId)
	}, [conv, rootId, status])

	// The replies are held while the pane is open and given back half an hour after it closes
	// (`state/trim.ts`): a reader who opens thirty threads over a morning holds one of them, and the
	// pane reopened on a cold thread fetches its newest page, which is what opening one does anyway.
	useEffect(() => visitThread(rootId), [rootId])

	// A pane opened by a permalink lands on the reply the link named, once that reply is in the
	// window; a pane opened from a row lands by the rule below.
	const replies = thread?.rows ?? NO_REPLIES
	useEffect(() => {
		if (highlightSeq === undefined || highlightSeq === 0) return
		if (!replies.some((reply) => reply.seq === highlightSeq)) return
		listRef.current?.scrollToSeq(highlightSeq, "center", true)
	}, [highlightSeq, replies])

	/*
	 * Where the reader stopped, as one seq: `threadReadSeq` (`state/selectors.ts`), the same watermark
	 * the footer's dot is decided by — the later of the conversation's read cursor and the reader's own
	 * last reply here — because a thread has no cursor of its own to ask.
	 */
	const mark = useModel((model) => threadReadSeq(model, conv, rootId))
	/*
	 * The first reply the reader has not had, decided once and then held for as long as this pane is
	 * on this root: the conversation's cursor moves while the pane is open — reading the timeline
	 * behind it is enough — and a rule that followed it would take itself off the screen under the
	 * reader's eyes. 0 means "nothing new", which is the pane's other landing.
	 *
	 * It is state adjusted during the render that first has the thread, which is the shape React
	 * documents for a value derived from data that arrives later: the second pass runs before the
	 * browser paints, so the rule and the landing are in the same frame and neither flashes.
	 */
	const [opened, setOpened] = useState<{ rootId: number; newFrom: number } | null>(null)
	if (opened?.rootId !== rootId && status === "ready" && root !== undefined) {
		setOpened({ rootId, newFrom: replies.find((reply) => reply.seq > mark)?.seq ?? 0 })
	}
	const newFrom = opened?.rootId === rootId ? opened.newFrom : 0

	const openProfile = useCallback(
		(userId: number) => details.open({ view: "core.profile", params: { userId } }),
		[details],
	)

	// The same three memos the timeline's context is assembled from, and for the same reason: the pane
	// draws message bodies, and a context rebuilt because some other conversation moved re-renders
	// every one of them (`render/renderContext.ts`).
	const renderContext = useRenderContextValue(navigateTo, openProfile)

	const pending = useMemo(
		() => outbox.filter((entry) => entry.target === `c:${conv}:t:${rootId}`),
		[outbox, conv, rootId],
	)
	const rows = useMemo(() => {
		const built = buildRows(replies, {
			// The pane draws its own *New replies* rule below and does not ask `buildRows` for one:
			// the divider it makes is keyed on a conversation's read cursor, and it drops the line
			// exactly where a thread needs it most — when every loaded reply is unread and older
			// history stands above them, which in a thread is the ordinary case, not the odd one.
			unreadAnchorSeq: 0,
			// A thread has no history-start divider of its own: paged to its beginning it still
			// opens under its root rather than under a second boundary (UX §4.1 R3).
			visibleFromSeq: 0,
			hasMoreBefore: true,
			pending,
			dayKey: formatDate,
		})
		// The root is the pane's first row, so a "Today" between it and the first reply of the same
		// day says nothing (X-A3 P9).
		const first = built[0]
		const replyRows =
			root !== undefined &&
			first?.kind === "day" &&
			formatDate(first.dayStartMs) === formatDate(root.createdAt)
				? built.slice(1)
				: built
		/*
		 * The root and the boundary below it are rows of the same scroller as the replies (§7.5).
		 * The boundary is a `historyStart` row — the one kind `buildRows` never produces here,
		 * because the pane always asks it for `hasMoreBefore: true` — and it is what that kind
		 * says: the top of the reply history, with the way further back drawn in it.
		 */
		const head: Row[] = []
		if (root !== undefined) {
			head.push({ kind: "message", seq: root.seq, msg: root, continues: false, timeGap: false })
		}
		head.push({ kind: "historyStart", visibleFromSeq: 0 })
		// The rule the pane lands on, directly above the first reply this reader has not had — under
		// the day pill where there is one, because the pill is about the day and the rule is about
		// the reply. The reply below it opens a block of its own: every divider is a break, and a
		// continuation row under the rule would be a reply with no name over it (`buildRows`).
		const withRule: Row[] = []
		for (const row of replyRows) {
			if (newFrom !== 0 && row.kind === "message" && row.seq === newFrom) {
				withRule.push({ kind: "unread", seq: newFrom })
				withRule.push({ ...row, continues: false, timeGap: false })
				continue
			}
			withRule.push(row)
		}
		return [...head, ...withRule]
	}, [replies, pending, root, newFrom])

	/*
	 * The landing, once the replies are in and before the paint. With new replies it is the first of
	 * them, at the top of the pane with the rule and a line of context above it (`scrollToSeq`'s
	 * `start` leaves 96 px); with none it is the foot of the list, which is the newest reply, and the
	 * root is then one scroll up.
	 *
	 * The two are over at different moments, and both have to survive a pane that is still being laid
	 * out. At the foot, `scrollToBottom` re-arms the list's own stick, so every later growth is
	 * followed for free and one call is enough. On a rule there is nothing to follow, so the landing
	 * repeats until the pane really overflows: a thread whose replies, pictures or code blocks are
	 * still growing is one the list has just measured as being at its bottom, and the next growth
	 * would take the reader there instead of leaving them on the rule. `atBottom()` is that
	 * measurement (measured on K=35: without it, a thread that overflowed by 90 px opened 90 px past
	 * its landing). Afterwards the list's own rules hold and a reply arriving under a reader moves
	 * nothing.
	 */
	const landedOn = useRef(0)
	useLayoutEffect(() => {
		if (landedOn.current === rootId) return
		if (highlightSeq !== undefined && highlightSeq !== 0) {
			landedOn.current = rootId
			return
		}
		if (root === undefined || status !== "ready") return
		if (newFrom === 0) {
			// The newest reply, asked for rather than left to the list's own anchoring: `scrollToBottom`
			// re-arms the stick, so the pane stays on the newest reply while the replies above it are
			// still being laid out — a thread whose faces arrive late measured 100 px short of its own
			// foot, with the pill offering a "latest" that was already on the screen.
			listRef.current?.scrollToBottom()
			landedOn.current = rootId
			return
		}
		listRef.current?.scrollToSeq(newFrom, "start")
		if (listRef.current?.atBottom() === false) landedOn.current = rootId
	}, [rows, root, rootId, status, highlightSeq, newFrom])

	const announce = useCallback((text: string) => setAnnouncement(text), [])
	const sources = useMemo(() => chatSourcesFor(conv), [conv])
	const preview = useCallback(
		async (markdown: string) => (await previewMarkdown(conv, markdown)).render,
		[conv],
	)

	// The row menu, from the one builder the timeline uses: a reply has no thread of its own, so no
	// `openThread` is passed and the entry is not drawn (MessageRow draws no affordance whose caller
	// is missing). Everything else — the copies, the edit, the delete, the profile — is the same
	// menu the timeline offers, because it is the same object.
	const actions = useMessageActions(conv, {
		place: meta,
		users,
		edit: (msg) => setEditing(msg.id),
		remove: (msg) => setConfirmDelete(msg),
		toggleSelect: (msg) =>
			setSelected((ids) =>
				ids.includes(msg.id) ? ids.filter((id) => id !== msg.id) : [...ids, msg.id],
			),
		openProfile,
		readOnly: !member,
	})

	const send = useCallback(
		async (markdown: string, attachments: AttachmentRef[]) => {
			try {
				await deps.send({ conv, markdown, attachments, threadRootId: rootId, alsoToChannel })
				setAlsoToChannel(false)
			} catch {
				announce(t("chat.error.sendUnavailable"))
			}
		},
		[deps, conv, rootId, alsoToChannel, announce],
	)

	const replyCount = root?.replyCount ?? replies.length
	const hasMoreBefore = thread?.hasMoreBefore === true

	const renderRow = useCallback(
		(row: Row) => {
			switch (row.kind) {
				case "day":
					return (
						<div className="tb-divider tb-divider--day">
							<time dateTime={isoDateTime(row.dayStartMs)}>
								{dayLabel(row.dayStartMs, Date.now())}
							</time>
						</div>
					)
				case "historyStart":
					return (
						<>
							<div className="tb-divider tb-divider--thread">
								{replyCount === 0
									? t("chat.thread.empty")
									: t("chat.thread.replies", { count: replyCount })}
							</div>
							{hasMoreBefore ? (
								<Button
									className="tb-thread__earlier"
									variant="neutral"
									size="sm"
									label={t("chat.thread.loadEarlier")}
									onClick={() => void loadOlderReplies(conv, rootId)}
								/>
							) : null}
						</>
					)
				case "unread":
					// The timeline's own divider, worded for a thread: what is new here is replies,
					// and the pane has just landed the reader on the first of them.
					return <div className="tb-divider tb-divider--unread">{t("chat.thread.newReplies")}</div>
				case "pending":
					return (
						<PendingRow
							entry={pending.find((item) => item.clientMsgId === row.clientMsgId)}
							authorId={meId}
							authorName={users[meId]?.displayName ?? ""}
						/>
					)
				case "message": {
					const { msg } = row
					if (editing === msg.id && deps.edit) {
						return (
							<MessageEditor
								msg={msg}
								sources={sources}
								preview={preview}
								onClose={() => setEditing(0)}
							/>
						)
					}
					return (
						<MessageRow
							msg={msg}
							continues={row.continues}
							showTime={row.timeGap}
							authorName={users[msg.authorId ?? 0]?.displayName ?? ""}
							mine={msg.authorId === meId}
							selected={selected.includes(msg.id)}
							actions={actions}
							// The root's own footer would point at this pane.
							hideThreadFooter={msg.id === rootId}
						/>
					)
				}
				default:
					// A thread has no gone divider: `buildRows` is not asked for one, so the kind
					// never occurs here.
					return null
			}
		},
		[
			editing,
			deps,
			sources,
			preview,
			users,
			meId,
			selected,
			actions,
			pending,
			conv,
			rootId,
			replyCount,
			hasMoreBefore,
		],
	)

	/*
	 * One state at a time (X-B3 D3). Until the root is here there is no thread: a read that failed is
	 * the alert with the way to try again, and one still running is the skeleton — never both, and
	 * never beside "No replies yet." and a reply box named after nobody. A `?thread=` that names a
	 * message this reader may not see, or one that is gone, is a failed read and lands here.
	 */
	if (root === undefined) {
		return (
			<section className="tb-thread tb-thread--state">
				{status === "error" ? (
					<ListState.Error
						message={t("chat.error.historyFailed")}
						retry={() => void loadThread(conv, rootId)}
					/>
				) : (
					<ListState.Loading rows={3} />
				)}
			</section>
		)
	}

	const rootAuthor = users[root.authorId ?? 0]?.displayName ?? ""
	// The root is a row of the list like any other and can be chosen like one, so the bar counts it.
	const selectedMessages = [root, ...replies].filter((msg) => selected.includes(msg.id))
	return (
		<RenderContextProvider value={renderContext}>
			{/* The pane is unnamed on purpose: the shell's own header above it is the `h2` "Thread"
			    and the column is already the labelled `complementary` region, so a name here would
			    be a second landmark saying the same thing. */}
			<section className="tb-thread">
				{/* A failure with a root already on screen is a page of *older* replies that did not
				    arrive: the thread stays, and the sentence stands over it. */}
				{status === "error" ? <p role="alert">{t("chat.error.historyFailed")}</p> : null}

				{selectedMessages.length > 0 ? (
					<SelectionBar
						count={selectedMessages.length}
						onCopy={() => actions.copyMany(selectedMessages)}
						onClear={() => setSelected(EMPTY_SELECTION)}
					/>
				) : null}

				<MessageList
					rows={rows}
					renderRow={renderRow}
					onReachTop={() => void loadOlderReplies(conv, rootId)}
					onReachBottom={() => undefined}
					onVisibleRangeChange={() => undefined}
					stickToBottom={highlightSeq === undefined || highlightSeq === 0}
					handleRef={listRef}
					meId={meId}
					onEscape={() => composerRef.current?.focus()}
				/>

				<ThreadTypingLine rootId={rootId} />

				{/*
				 * The box says "Reply …" and the group around it says what the reply answers, so a
				 * screen reader entering the pane's foot hears "Reply to Anna's thread in
				 * #allgemein" instead of the sixth control on the screen called "Reply in thread"
				 * (X-A2 D9). A root whose author the model has not filled in — a system message, an
				 * account that is gone — names the place alone rather than saying "Reply to 's
				 * thread" (X-B3 D3). The name belongs on the box itself; `ComposerProps` has no way
				 * to carry one yet, and the hunk that gives it one is Q-04's.
				 */}
				{member ? (
					<div
						role="group"
						aria-label={
							rootAuthor === ""
								? t("chat.thread.composerLabelIn", { conversation: conversationName })
								: t("chat.thread.composerLabel", {
										author: rootAuthor,
										conversation: conversationName,
									})
						}
					>
						<Composer
							targetKey={`c:${conv}:t:${rootId}`}
							placeholderKey="chat.thread.placeholder"
							sources={sources}
							onSend={send}
							preview={preview}
							renderPreview={MarkdownPreview}
							// EDITOR §13.2: the box the pane opens with takes the caret, and it is the one
							// mechanism that does — the pane's `ref` only puts focus back into it.
							// eslint-disable-next-line jsx-a11y/no-autofocus
							autoFocus
							compact
							// The column is ~350 px wide whatever the window is, so the ten-button row
							// wraps: the pane's toolbar lives behind the fold's toggle (EDITOR §7.1).
							toolbar="overflow"
							renderEmojiPicker={EmojiPicker}
							ref={composerRef}
							/*
							 * Where this reply goes, inside the frame and under its tools: the
							 * destination is part of what the send does, and a tick that stayed ticked
							 * would quietly turn a thread into a second channel (X-A1 A4, §7.5).
							 */
							belowTools={
								<label className="tb-check tb-thread__also">
									<input
										type="checkbox"
										checked={alsoToChannel}
										onChange={(event) => setAlsoToChannel(event.target.checked)}
									/>
									<span className="tb-check__label">
										{t("chat.thread.alsoSend", { conversation: conversationName })}
									</span>
								</label>
							}
						/>
					</div>
				) : (
					<JoinBanner conv={conv} />
				)}

				<DeleteConfirm
					message={confirmDelete}
					onCancel={() => setConfirmDelete(null)}
					onConfirm={(msg) => {
						setConfirmDelete(null)
						void deps.remove?.({ id: msg.id })
					}}
				/>

				<LiveRegion label={t("chat.thread.status")}>{announcement}</LiveRegion>
			</section>
		</RenderContextProvider>
	)
}
